// Service for fetching recent activity from GitHub and YouTube APIs

const GITHUB_USERNAME = 'TEHAQUE'; // Main GitHub profile
const GITHUB_ORG = 'TwoHandStudio'; // Organization for QuickBlocksUI
const GITHUB_TOKEN = process.env.REACT_APP_GITHUB_TOKEN; // Optional GitHub token to avoid 403 rate limit
const YOUTUBE_CHANNEL_HANDLE = '@tehaque'; // YouTube channel handle

/**
 * Fetch recent GitHub activity (commits, releases, etc.)
 * Uses GitHub Events API: https://docs.github.com/en/rest/activity/events
 */
export const fetchGitHubActivity = async () => {
  try {
    console.log('🔍 Fetching GitHub activity...');
    console.log('GitHub Username:', GITHUB_USERNAME);
    console.log('GitHub Org:', GITHUB_ORG);
    console.log('Has GitHub Token:', !!GITHUB_TOKEN);

    const headers = {
      'Accept': 'application/vnd.github.v3+json',
      'User-Agent': 'Tehquedev-App'
    };
    if (GITHUB_TOKEN) {
      headers['Authorization'] = `token ${GITHUB_TOKEN}`;
    }
    
    // Fetch from both personal profile and organization
    const [personalEvents, orgEvents, quickBlocksReleases] = await Promise.all([
      fetch(`https://api.github.com/users/${GITHUB_USERNAME}/events/public`, { headers }),
      fetch(`https://api.github.com/orgs/${GITHUB_ORG}/events`, { headers }),
      fetch(`https://api.github.com/repos/${GITHUB_ORG}/QuickBlocksUI/releases`, { headers })
    ]);
    
    console.log('📊 API Response Status:');
    console.log('Personal Events:', personalEvents.status, personalEvents.ok);
    console.log('Org Events:', orgEvents.status, orgEvents.ok);
    console.log('QuickBlocks Releases:', quickBlocksReleases.status, quickBlocksReleases.ok);
    
    const activities = [];
    
    // Process personal GitHub events
    if (personalEvents.ok) {
      const events = await personalEvents.json();
      console.log('👤 Personal Events Count:', events.length);
      console.log('👤 Personal Events Sample:', events.slice(0, 2));
      
      const relevantEvents = events
        .filter(event => ['PushEvent', 'CreateEvent', 'ReleaseEvent', 'PublicEvent'].includes(event.type))
        .slice(0, 2) // Get latest 2 events
        .map(event => {
          const repo = event.repo.name;
          const date = new Date(event.created_at);
          
          switch (event.type) {
            case 'PushEvent':
              const commitCount = event.payload.commits?.length || 1;
              return {
                type: 'github',
                title: `${commitCount} commit${commitCount > 1 ? 'y' : ''} w ${repo}`,
                desc: `Najnowsze zmiany w kodzie`,
                linkTo: `https://github.com/${repo}`,
                date: date
              };
            
            case 'CreateEvent':
              if (event.payload.ref_type === 'repository') {
                return {
                  type: 'github',
                  title: `Nowe repozytorium: ${repo}`,
                  desc: `Nowy projekt został utworzony`,
                  linkTo: `https://github.com/${repo}`,
                  date: date
                };
              }
              return null;
            
            case 'ReleaseEvent':
              return {
                type: 'github',
                title: `Wydano ${event.payload.release.tag_name} dla ${repo}`,
                desc: event.payload.release.name || 'Nowa wersja została wydana',
                linkTo: event.payload.release.html_url,
                date: date
              };
            
            case 'PublicEvent':
              return {
                type: 'github',
                title: `${repo} jest teraz publiczne`,
                desc: `Repozytorium jest teraz open source`,
                linkTo: `https://github.com/${repo}`,
                date: date
              };
            
            default:
              return null;
          }
        })
        .filter(Boolean);
      
      console.log('👤 Personal Relevant Events:', relevantEvents);
      activities.push(...relevantEvents);
    } else {
      if (personalEvents.status === 403) {
        console.warn('⚠️ Personal Events API 403 (rate limit or auth). Kontynuuję bez tych danych.');
      } else {
        console.warn('⚠️ Personal Events API failed:', personalEvents.status);
      }
    }

    // Process organization events
    if (orgEvents.ok) {
      const events = await orgEvents.json();
      console.log('🏢 Org Events Count:', events.length);
      console.log('🏢 Org Events Sample:', events.slice(0, 2));
      
      const relevantOrgEvents = events
        .filter(event => ['PushEvent', 'CreateEvent', 'ReleaseEvent', 'PublicEvent'].includes(event.type))
        .slice(0, 2) // Get latest 2 events
        .map(event => {
          const repo = event.repo.name;
          const date = new Date(event.created_at);
          
          switch (event.type) {
            case 'PushEvent':
              const commitCount = event.payload.commits?.length || 1;
              return {
                type: 'github',
                title: `${commitCount} commit${commitCount > 1 ? 'y' : ''} w ${repo}`,
                desc: `Najnowsze zmiany w organizacji`,
                linkTo: `https://github.com/${repo}`,
                date: date
              };
            
            case 'CreateEvent':
              if (event.payload.ref_type === 'repository') {
                return {
                  type: 'github',
                  title: `Nowe repozytorium: ${repo}`,
                  desc: `Nowy projekt organizacji został utworzony`,
                  linkTo: `https://github.com/${repo}`,
                  date: date
                };
              }
              return null;
            
            case 'ReleaseEvent':
              return {
                type: 'github',
                title: `Wydano ${event.payload.release.tag_name} dla ${repo}`,
                desc: event.payload.release.name || 'Nowa wersja została wydana',
                linkTo: event.payload.release.html_url,
                date: date
              };
            
            default:
              return null;
          }
        })
        .filter(Boolean);
      
      console.log('🏢 Org Relevant Events:', relevantOrgEvents);
      activities.push(...relevantOrgEvents);
    } else {
      if (orgEvents.status === 403) {
        console.warn('⚠️ Org Events API 403 (rate limit or auth). Kontynuuję bez tych danych.');
      } else {
        console.warn('⚠️ Org Events API failed:', orgEvents.status);
      }
    }
    
    // Process QuickBlocksUI releases specifically
    if (quickBlocksReleases.ok) {
      const releases = await quickBlocksReleases.json();
      console.log('📦 QuickBlocks Releases Count:', releases.length);
      console.log('📦 Latest Release:', releases[0]);
      
      const latestRelease = releases[0]; // Get the most recent release
      
      if (latestRelease) {
        activities.push({
          type: 'npm',
          title: `QuickBlocksUI ${latestRelease.tag_name}`,
          desc: `Nowa wersja paczki npm została wydana`,
          linkTo: latestRelease.html_url,
          date: new Date(latestRelease.published_at)
        });
      }
    } else {
      if (quickBlocksReleases.status === 403) {
        console.warn('⚠️ QuickBlocks Releases API 403 (rate limit or auth).');
      } else {
        console.warn('⚠️ QuickBlocks Releases API failed:', quickBlocksReleases.status);
      }
    }
    
    console.log('🎯 Final Activities Array:', activities);
    return activities;
  } catch (error) {
    console.error('Error fetching GitHub activity:', error);
    return [];
  }
};

/**
 * Fetch recent YouTube uploads using channel handle
 * Uses YouTube Data API v3: https://developers.google.com/youtube/v3/docs
 */
export const fetchYouTubeActivity = async () => {
  // Embedded YouTube player - bez API, po prostu najnowszy film
  console.log('🎥 Using embedded YouTube player');
  
  // Dodajemy różne daty żeby YouTube nie zawsze był na górze
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  return [
    {
      type: 'youtube',
      title: 'Najnowszy film na kanale',
      desc: 'Sprawdź najnowsze treści na moim kanale YouTube',
      linkTo: `https://www.youtube.com/${YOUTUBE_CHANNEL_HANDLE}`,
      date: yesterday.toLocaleDateString('pl-PL'), // Wczoraj żeby nie dominował
      // Dodajemy embed URL dla najnowszego filmu
      embedUrl: `https://www.youtube.com/embed?listType=user_uploads&list=${YOUTUBE_CHANNEL_HANDLE.replace('@', '')}`
    }
  ];
};

/**
 * Combine and sort activities from both platforms
 */
export const fetchRecentActivity = async () => {
  try {
    const [githubActivity, youtubeActivity] = await Promise.all([
      fetchGitHubActivity(),
      fetchYouTubeActivity()
    ]);
    
    console.log('🔄 Combining activities:');
    console.log('📊 GitHub activities:', githubActivity.length);
    console.log('📺 YouTube activities:', youtubeActivity.length);
    
    // Combine activities and sort by date (newest first)
    const allActivities = [...githubActivity, ...youtubeActivity]
      .sort((a, b) => {
        // Konwertuj daty na timestamp dla lepszego sortowania
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        return dateB - dateA; // Najnowsze pierwsze
      })
      .slice(0, 3); // Keep only the 3 most recent
    
    console.log('📋 Final mixed activities:', allActivities.length);
    console.log('📋 Activities order:', allActivities.map(a => `${a.type}: ${a.title}`));
    
    return allActivities;
  } catch (error) {
    console.error('Error fetching combined activity:', error);
    return getFallbackActivities().slice(0, 3);
  }
};

/**
 * Fetch NPM package download statistics
 * Uses NPM Registry API: https://api.npmjs.org/downloads/range/
 * Fetches all-time data since package inception (2022-09-08) by splitting into 1-year windows
 */
export const fetchNpmStats = async (packageName = '@tehaque/react.js-components', timeRange = null) => {
  try {
    console.log('📦 Fetching NPM stats for:', packageName);

    // npm API supports max 1 year per request, so split into yearly windows
    const endDate = new Date();
    // Both packages started around September 2022
    const startDate = timeRange
      ? new Date(timeRange.split(':')[0])
      : new Date('2022-09-08');

    // Build list of 1-year windows covering the full period
    const windows = [];
    let windowStart = new Date(startDate);
    while (windowStart < endDate) {
      const windowEnd = new Date(windowStart);
      windowEnd.setFullYear(windowEnd.getFullYear() + 1);
      windowEnd.setDate(windowEnd.getDate() - 1);
      windows.push({
        start: windowStart.toISOString().split('T')[0],
        end: (windowEnd < endDate ? windowEnd : endDate).toISOString().split('T')[0]
      });
      windowStart = new Date(windowEnd);
      windowStart.setDate(windowStart.getDate() + 1);
    }

    console.log(`📅 Fetching ${windows.length} windows for ${packageName}:`, windows.map(w => `${w.start}:${w.end}`));

    // Fetch all windows in parallel
    const responses = await Promise.all(
      windows.map(w =>
        fetch(`https://api.npmjs.org/downloads/range/${w.start}:${w.end}/${packageName}`)
          .then(r => r.ok ? r.json() : null)
          .catch(() => null)
      )
    );

    const allDownloads = responses.flatMap(data => (data?.downloads || []));
    const resolvedPackageName = responses.find(d => d?.package)?.package || packageName;
    const totalDownloads = allDownloads.reduce((sum, day) => sum + day.downloads, 0);

    console.log(`📊 ${packageName} total downloads (all-time): ${totalDownloads}`);

    return {
      package: resolvedPackageName,
      totalDownloads,
      timeRange: `${startDate.toISOString().split('T')[0]}:${endDate.toISOString().split('T')[0]}`,
      dailyStats: allDownloads,
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    };
  } catch (error) {
    console.error('Error fetching NPM stats:', error);
    return null;
  }
};

/**
 * Fetch multiple NPM packages stats
 */
export const fetchMultipleNpmStats = async (packages = ['@tehaque/react.js-components', '@twohandstudio/quickblocksui']) => {
  try {
    console.log('📦 Fetching stats for multiple packages:', packages);
    
    const statsPromises = packages.map(pkg => fetchNpmStats(pkg));
    const results = await Promise.all(statsPromises);
    
    return results.filter(result => result !== null);
  } catch (error) {
    console.error('Error fetching multiple NPM stats:', error);
    return [];
  }
};

/**
 * Fallback static activities (used when API calls fail)
 */
export const getFallbackActivities = () => [
  {
    type: "npm",
    title: "QuickBlocksUI v1.2.0",
    desc: "Nowa wersja biblioteki React z ulepszonymi komponentami",
    linkTo: "https://github.com/TwoHandStudio/QuickBlocksUI"
  },
  {
    type: "youtube",
    title: "Nowy tutorial na YouTube",
    desc: "Najnowszy film o programowaniu i rozwoju aplikacji",
    linkTo: "https://www.youtube.com/@tehaque"
  },
  {
    type: "github",
    title: "Aktualizacja projektu",
    desc: "Najnowsze zmiany w kodzie i nowe funkcjonalności",
    linkTo: "https://github.com/TEHAQUE"
  }
];